/* eslint-disable react/jsx-pascal-case */
import { golf } from './enums';
import { NamespaceConsumer, NamespaceShape, withNamespace } from '@ux/namespace-component';
import { IntlProvider, FormattedMessage } from 'react-intl';
import OrderConfirmation from '@ux/order-confirmation';
import Notifications from '@ux/notifications'; // new endpoint
import LegacyNotifications from '@ux/notifs-v1'; // old endpoint
import Icon from '@ux/icon';
import InappHelp from '@ux/inapp-help';
import Cart from '@ux/cart';
import Logo from '@ux/logo';
import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import AppName from './app-name';
import { User } from './account-tray';
import NavigationSet from './nav-set';

/**
 * Render Version.
 *
 * @class Version
 * @api public
 */
class Version extends NamespaceConsumer {
  render() {
    return (
      <Fragment>
        { this.props.value }
      </Fragment>
    );
  }
}

Version.propTypes = {
  ...NamespaceShape,
  value: PropTypes.string
};

const NamespacedVersion = withNamespace(Version);

/**
 * Render Application Header Footer.
 *
 * @class NavigationTop
 * @api public
 */
class NavigationTop extends NamespaceConsumer {
  /**
   * Determine whether to render legacy notifications or the current module
   * @returns {React.element} legacy notifications component or current
   */
  renderNotifications() {
    const props = this.props;
    const { privateLabelId, urls } = props;
    const notificationMessages = Object.keys(golf.notifications).reduce((memo, key) => {
      memo[key] = <FormattedMessage id={ golf.notifications[key] } />;
      return memo;
    }, {});

    if (props.notificationsVersion === 2 && props.features.notifsV2) {
      return (
        <Notifications
          manifest={ props.manifest }
          sso={ urls.sso }
          market={ props.market }
          shopperId={ props.shopperId }
          privateLabelId={ privateLabelId }
          messages={ notificationMessages }
          tooltip={ props.messages[golf.notifications.headerText] }
          showCount={ false }
          showOpenCaret={ true }
          { ...urls.notifications } />
      );
    }
    return (
      <LegacyNotifications
        manifest={ props.manifest }
        sso={ urls.sso }
        market={ props.market }
        shopperId={ props.shopperId }
        privateLabelId={ privateLabelId }
        messages={ notificationMessages }
        tooltip={ props.messages[golf.notifications.headerText] }
        { ...urls.notifications } />
    );
  }

  /**
   * Render help, cart, notifications, and account tray. ORDER MATTERS.
   * @returns {[React.element]} Array of components for each global element
   */
  renderGlobalElements() {
    const props = this.props;
    const hasItems = !!props.items;
    const loggedIn = props.loggedIn;
    const isInternal = props.preset === 'internal-header';
    const isPass = props.preset === 'pass-header';
    const elements = [];
    const isApiReseller = props.privateLabelType === 3;

    if (!isInternal && (!isApiReseller || (props.resellerUrls && props.resellerUrls.help))) {
      elements.push(<li key='help' className={ this.namespace('nav-item') }>
        <InappHelp
          enabled={ props.features.inappHelp }
          url={ props.urls.help.href }
          ref={ c => { this.inappHelp = c; } }
          label={ <Icon name='help-header' /> }
          title={ <FormattedMessage id={ golf.inApp } /> }
          tooltip={ props.messages[golf.inApp] } />
      </li>);
    }

    if (hasItems && !isPass && !isApiReseller) {
      elements.push(<li key='cart' className={ this.namespace(props.className, props.hidden, 'nav-item') }>
        <Cart
          items={ props.items }
          market={ props.market }
          messages={ props.messages }
          checkout={ props.urls.checkout.href }
          className={ this.namespace('nav-link', 'nav-link-cart') } />
      </li>);
    }

    if (loggedIn && !isPass && !isInternal) {
      elements.push(<li key='notifications' className={ this.namespace('nav-item') }>
        { this.renderNotifications() }
      </li>);
    }

    if (!isPass) {
      elements.push(<li key='account-tray' className={ this.namespace('nav-item') }>
        <User { ...props } />
      </li>);
    }

    return elements;
  }

  /**
   * Renders left nav elements: logo, home button, app name.
   * @returns {React.element} The div encompassing left nav elements
   */
  renderLeftNav() {
    const props = this.props;
    const { privateLabelId, isMobile, isTablet, orderId, waffleLeft, appName, app, showWaffleMenu, messages, version } = props;
    const isNotDesktop = isMobile || isTablet;
    const logoClasses = this.namespace(this.classNames('logo-text', {
      'logo-gd': privateLabelId === 1
    }));
    const name = (['riviera', 'mya', 'go', 'account'].indexOf(app) >= 0) ?
      messages['PC:AppHeader:Header:Home'] : appName;
    const showWaffleLeft = waffleLeft && showWaffleMenu;

    // Needed for some GD Only css tweaks on mobile and phablet.
    const outsideLogoClasses = this.namespace(this.classNames('logo', {
      'brand-logo': privateLabelId === 1,
      'waffle-left-logo': showWaffleLeft
    }));
    return (<div className={ this.namespace('nav-left-wrap') }>
      { orderId && <OrderConfirmation orderId={ orderId } /> }
      {
        isNotDesktop && <NavigationSet renderMobile={ isNotDesktop } navigationPosition='top' { ...props } />
      }
      <Logo
        className={ outsideLogoClasses }
        country={ props.countryName }
        href={ props.urls.home.href }
        market={ props.market }
        messages={ props.messages }
        logos={ props.logos }
        privateLabelId={ privateLabelId }
        privateLabelName={ props.privateLabelName }
        showCountryNameOnLogo={ false }
        spanClass={ logoClasses }
        urlArgs={ props.urlArgs }
        isMobile={ isMobile }
        includeGDHead={ false }
        data-eid='uxp.hyd.app_header.gd_logo.link.click' />
      <div className={ this.namespace(this.classNames('nav', 'nav-left', { 'waffle-left-control': showWaffleLeft })) }>
        <AppName { ...props } name={ name } />
      </div>
      {
        showWaffleLeft &&
          <span className={ this.namespace(this.classNames('product-name', 'waffle-menu', { 'waffle-left-product-name': showWaffleLeft })) }>
            { name }
          </span>
      }
      {
        version &&
        <div className={ this.namespace(this.classNames('version', 'hidden-sm-down', { 'waffle-left-version': showWaffleLeft })) }>
          <NamespacedVersion value={ props.version } />
        </div>
      }
    </div>);
  }

  render() {
    const { market, messages, isMobile, isTablet, navigationTop } = this.props;
    // safari only stuff :(
    let isThisSafari = false;
    if (typeof navigator !== 'undefined') {
      isThisSafari = navigator.userAgent.indexOf('Safari') !== -1 && navigator.userAgent.indexOf('Chrome') === -1;
    }

    return (
      <IntlProvider locale={ market } messages={ messages }>
        <nav className={ this.namespace(this.classNames('nav-top', 'clearfix', { 'safari-only': isThisSafari })) }>
          <div className={ this.namespace('container') }>
            <div className={ this.namespace('d-flex', 'flex-row') }>
              { this.renderLeftNav() }
              <ul className={ this.namespace('nav', 'nav-right') }>
                { !isMobile && !isTablet && navigationTop &&
                  <li className={ this.namespace('nav-item') }>
                    <div>
                      <a className={ this.namespace('custom-link') }
                        href={ navigationTop.href }
                        id={ navigationTop.id }
                        target={ navigationTop.target }
                        onClick={ navigationTop.onClick }
                        data-eid={ navigationTop.eid }>
                        { navigationTop.caption }
                      </a>
                    </div>
                  </li>
                }
                { !isMobile && !isTablet && navigationTop &&
                  <li className={ this.namespace('custom-links-divider') } />
                }
                { this.renderGlobalElements() }
              </ul>
            </div>
          </div>
        </nav>
      </IntlProvider>
    );
  }
}

NavigationTop.update = function update(nav, done = () => {}) {
  if (Array.isArray(nav) && nav.length === 1) {
    return this.setState({
      navigationTop: nav[0]
    }, done);
  }
};

NavigationTop.updateHelpUrl = function updateHelpUrl(helpUrl, done = () => {}) {
  return this.setState({
    helpUrl
  }, done);
};

/**
 * Default properties for the top half of the navigation.
 *
 * @type {Object}
 * @api public
 */
NavigationTop.defaultProps = {
  app: 'domainmanager',
  features: {},
  preset: 'application-header',
  loggedIn: false,
  manifest: 'applicationheader',
  market: 'en-US',
  privateLabelId: 1,
  privateLabelName: 'GoDaddy.com',
  proshopper: false,
  urlArgs: {},
  sso: {
    app: 'www',
    path: ''
  },
  urls: {
    notifications: {
      api: { href: 'https://mya.dev-godaddy.com/webapi/notifications' },
      cache: { href: '//img1.wsimg-com.ide/mya/notifications/cache.html' },
      endpoint: { href: 'https://notifications.api.cloud.dev-godaddy.com/v1/notifications' }
    },
    help: { href: 'https://www.godaddy.com/help' },
    home: { href: 'https://www.godaddy.com' },
    contactUs: { href: 'https://www.godaddy.com/contact-us.aspx' },
    checkout: { href: 'https://cart.godaddy.com' },
    renewals: { href: 'https://account.godaddy.com/billing?filter=expires&subFilter=90' },
    account: { href: 'https://account.godaddy.com' },
    products: { href: 'https://account.godaddy.com/products' },
    pro: { href: 'https://pro.godaddy.com/launch/managewp?landingScreen=pro-home' },
    proHomeApp: { href: 'https://pro.godaddy.com/launch/managewp?landingScreen=pro-home' }
  }
};

/**
 * Require properties.
 *
 * @type {Object}
 * @api public
 */
NavigationTop.propTypes = {
  ...NamespaceShape,
  account: PropTypes.string,
  app: PropTypes.string.isRequired,
  appName: PropTypes.string.isRequired,
  displayName: PropTypes.string,
  features: PropTypes.object,
  help: PropTypes.string,
  preset: PropTypes.string,
  isMobile: PropTypes.bool,
  isTablet: PropTypes.bool,
  items: PropTypes.number,
  loggedIn: PropTypes.bool,
  manifest: PropTypes.string,
  market: PropTypes.string.isRequired,
  messages: PropTypes.object.isRequired,
  name: PropTypes.string,
  navigationTop: PropTypes.object,
  orderId: PropTypes.string,
  privateLabelId: PropTypes.number,
  privateLabelName: PropTypes.string,
  products: PropTypes.string,
  proshopper: PropTypes.bool,
  shopperId: PropTypes.string,
  showWaffleMenu: PropTypes.bool,
  renewals: PropTypes.string,
  sso: PropTypes.object,
  urlArgs: PropTypes.object,
  urls: PropTypes.object.isRequired,
  version: PropTypes.string,
  vip: PropTypes.object
};

export {
  NamespacedVersion as Version
};

export default withNamespace(NavigationTop);
